// OverviewTab.tsx
// Overview dashboard with improved readability and actionable items

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Stack,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Refresh,
  Warning,
  CheckCircle,
  Event,
  CalendarToday,
  WorkOff,
  Schedule,
  MusicNote,
  OpenInNew,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MailOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';
import { isIrishHoliday, isWeekend, getHolidayInfo } from '../../../constants/irishHolidays';
import WhosWorkingToday from '../../../components/WhosWorkingToday';
import RotaWidget from '../../../components/RotaWidget';

interface StaffCompliance {
  staffId: string;
  name: string;
  email: string;
  employmentType: string;
  missingCurrentMonth: number;
  missingTotal: number;
  totalWorkdays: number;
  completionPercentage: number;
  missingDays?: string[];
}

interface CoverageWarning {
  date: string;
  coverageLevel: string;
  workingCount: number;
  totalStaff: number;
  coverageColor: string;
}

interface BalanceWarning {
  staffId: string;
  name: string;
  email: string;
  tilHours?: number;
  tilDays?: number;
  exceedsGuideline?: boolean;
  remainingDays?: number;
  totalDays?: number;
  percentageRemaining?: number;
  monthsUntilYearEnd?: number;
  hours?: number;
  expiryDate?: string;
  daysUntilExpiry?: number;
}

interface PerformanceInfo {
  performanceId: string;
  date: string;
  startTime: string;
  venue: string;
  bandName: string;
  staffCount: number;
  coverageLevel: string;
  status: string;
  staff?: Array<{
    staffId: string;
    name: string;
    role: string;
  }>;
}

interface OverviewData {
  rotaCompliance: {
    incompleteStaff: StaffCompliance[];
    compliantStaff: number;
    nonCompliantStaff: number;
  };
  leaveRequests: {
    pendingCount: number;
    approvedFutureCount: number;
    coverageWarnings: CoverageWarning[];
  };
  balanceWarnings: {
    highTIL: BalanceWarning[];
    highAL: BalanceWarning[];
    expiringTIL: BalanceWarning[];
  };
  performances: {
    upcoming: PerformanceInfo[];
    missingStaff: PerformanceInfo[];
  };
  artistStats: {
    totalArtists: number;
    recentlyAdded: number;
    withUpcomingPerformances: number;
  };
  nextBankHoliday: {
    name: string;
    date: string;
    daysAway: number;
  } | null;
  generatedAt: string;
}

const OverviewTab: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewData | null>(null);
  const [staffBalances, setStaffBalances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPerformance, setExpandedPerformance] = useState<string | null>(null);

  const [rotaEmailDialog, setRotaEmailDialog] = useState<{
    open: boolean; staffId: string; staffName: string; subject: string; message: string;
  }>({ open: false, staffId: '', staffName: '', subject: '', message: '' });
  const [rotaEmailSending, setRotaEmailSending] = useState(false);

  const openRotaEmail = (staff: StaffCompliance) => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      .toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    const monthAfter = new Date(now.getFullYear(), now.getMonth() + 2, 1)
      .toLocaleString('en-GB', { month: 'long', year: 'numeric' });

    setRotaEmailDialog({
      open: true,
      staffId: staff.staffId,
      staffName: staff.name,
      subject: 'Rota — Please Complete Your Schedule',
      message: `Hi ${staff.name.split(' ')[0]},\n\nThis is a reminder that your rota is currently ${staff.completionPercentage}% complete, with ${staff.missingTotal} working day${staff.missingTotal !== 1 ? 's' : ''} still missing across the next two months.\n\nAs a reminder, rotas must be kept at least two months in advance at all times. Please make sure ${nextMonth} and ${monthAfter} are fully filled in as soon as possible.\n\nYou can update your rota by logging in at your usual link.\n\nThanks,\nIMC Management`,
    });
  };

  const handleSendRotaEmail = async () => {
    setRotaEmailSending(true);
    try {
      await axiosInstance.post(`/api/personnel/${rotaEmailDialog.staffId}/leave/notify`, {
        subject: rotaEmailDialog.subject,
        message: rotaEmailDialog.message,
        type: 'annual-leave',
      });
      setRotaEmailDialog(d => ({ ...d, open: false }));
    } catch (err) {
      console.error('Failed to send rota email', err);
    } finally {
      setRotaEmailSending(false);
    }
  };

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📊 Fetching overview data...');
      
      const response = await axiosInstance.get('/api/dashboard/overview');
      setData(response.data);
      console.log('✅ Overview data loaded:', response.data);
    } catch (err: any) {
      console.error('❌ Error fetching overview:', err);
      setError(err.response?.data?.message || 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
  fetchOverview();
  fetchStaffBalances();
}, []);

  const fetchStaffBalances = async () => {
  try {
    console.log('📊 Fetching accurate staff balances...');
    const res = await axiosInstance.get('/api/personnel/staff/leave-balances');
    setStaffBalances(res.data);
  } catch (err) {
    console.error('❌ Error fetching staff balances:', err);
  }
};

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getCoverageChipColor = (level: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (level) {
      case 'adequate': return 'success';
      case 'limited': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const isWorkingDay = (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isWeekend(date) && !isIrishHoliday(date);
  };

  const getHolidayName = (dateString: string): string | null => {
    const date = new Date(dateString);
    const holidayInfo = getHolidayInfo(date);
    return holidayInfo ? holidayInfo.name : null;
  };

  // Filter coverage warnings to exclude bank holidays and weekends
  const filteredCoverageWarnings = data?.leaveRequests.coverageWarnings.filter(warning => 
    isWorkingDay(warning.date)
  ) || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchOverview}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No overview data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Refresh */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Dashboard Overview
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchOverview}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {/* ===== LEAVE YEAR ALERTS ===== */}
      {(() => {
        const now = new Date();
        const month = now.getMonth() + 1; // 1-12
        const day = now.getDate();
        const isJanuary = month === 1;
        const isYearEnd = month >= 10; // Oct, Nov, Dec

        // Staff with carryover days still active in January
        const staffWithCarryover = staffBalances.filter(p => {
          const carryover = p.leaveEntitlement?.annualLeaveCarryover || 0;
          const expiry = p.leaveEntitlement?.annualLeaveCarryoverExpiry;
          return carryover > 0 && expiry && new Date(expiry) > now;
        });

        // Staff with more than half their entitlement unused near year-end
        const staffHighAL = isYearEnd ? staffBalances.filter(p => {
          const total = p.leaveEntitlement?.annualLeaveCurrentYear || p.leaveEntitlement?.annualLeaveTotal || 0;
          const used = p.leaveEntitlement?.annualLeaveUsed || 0;
          const remaining = total - used;
          return remaining > total * 0.5;
        }) : [];

        return (
          <>
            {isJanuary && staffWithCarryover.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>January carry-over notice:</strong> {staffWithCarryover.length} staff member{staffWithCarryover.length !== 1 ? 's' : ''} still have unused carry-over days expiring Jan 31:{' '}
                {staffWithCarryover.map(p => {
                  const days = p.leaveEntitlement?.annualLeaveCarryover || 0;
                  return `${p.name} (${days}d)`;
                }).join(', ')}
              </Alert>
            )}
            {isYearEnd && staffHighAL.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Year-end leave check:</strong> {staffHighAL.length} staff member{staffHighAL.length !== 1 ? 's' : ''} still have more than half their annual leave unused:{' '}
                {staffHighAL.map(p => {
                  const total = p.leaveEntitlement?.annualLeaveCurrentYear || p.leaveEntitlement?.annualLeaveTotal || 0;
                  const used = p.leaveEntitlement?.annualLeaveUsed || 0;
                  return `${p.name} (${total - used}d left)`;
                }).join(', ')}
              </Alert>
            )}
          </>
        );
      })()}

      {/* Bank Holiday Alert */}
      {data.nextBankHoliday && (
        <Alert severity="info" icon={<Event />} sx={{ mb: 3 }}>
          <strong>Next Bank Holiday:</strong> {data.nextBankHoliday.name} on{' '}
          {new Date(data.nextBankHoliday.date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}{' '}
          ({data.nextBankHoliday.daysAway} day{data.nextBankHoliday.daysAway !== 1 ? 's' : ''} away)
        </Alert>
      )}


      {/* Quick Stats Row - FIXED TEXT CONTRAST */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Pending Requests */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                    {data.leaveRequests.pendingCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    Pending Requests
                  </Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 48, opacity: 0.9, color: 'white' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Non-Compliant Staff */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: data.rotaCompliance.nonCompliantStaff > 0 
              ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                    {data.rotaCompliance.nonCompliantStaff}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    Incomplete Rotas
                  </Typography>
                </Box>
                {data.rotaCompliance.nonCompliantStaff > 0 ? (
                  <Warning sx={{ fontSize: 48, opacity: 0.9, color: 'white' }} />
                ) : (
                  <CheckCircle sx={{ fontSize: 48, opacity: 0.9, color: 'white' }} />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Balance Warnings */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: '#1a1a1a'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    {data.balanceWarnings.highTIL.length + data.balanceWarnings.highAL.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(26,26,26,0.9)' }}>
                    Balance Warnings
                  </Typography>
                </Box>
                <WorkOff sx={{ fontSize: 48, opacity: 0.7, color: '#1a1a1a' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Performances */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#1a1a1a'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    {data.performances.upcoming.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(26,26,26,0.9)' }}>
                    Performances (30d)
                  </Typography>
                </Box>
                <Event sx={{ fontSize: 48, opacity: 0.7, color: '#1a1a1a' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

          <RotaWidget />
         <WhosWorkingToday />

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        
        {/* SECTION 1: Rota Compliance */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Schedule color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Rota Compliance
                </Typography>
              </Stack>
              
              {data.rotaCompliance.incompleteStaff.length === 0 ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  All staff have complete rotas! 🎉
                </Alert>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {data.rotaCompliance.nonCompliantStaff} staff member(s) need to complete their rotas
                  </Alert>
                  
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Staff</strong></TableCell>
                          <TableCell align="center"><strong>Current Month</strong></TableCell>
                          <TableCell align="center"><strong>2 Months</strong></TableCell>
                          <TableCell align="right"><strong>Progress</strong></TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.rotaCompliance.incompleteStaff.map((staff) => (
                          <TableRow key={staff.staffId} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {staff.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {staff.employmentType}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {staff.missingCurrentMonth > 0 ? (
                                <Tooltip
                                  title={
                                    staff.missingDays && staff.missingDays.length > 0
                                      ? staff.missingDays
                                          .filter(d => {
                                            const m = new Date(d).getMonth();
                                            return m === new Date().getMonth();
                                          })
                                          .map(d => new Date(d + 'T00:00:00').toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }))
                                          .join(', ') || 'No details'
                                      : 'No details'
                                  }
                                >
                                  <Chip label={`${staff.missingCurrentMonth} missing`} size="small" color="error" />
                                </Tooltip>
                              ) : (
                                <Chip label="Complete" size="small" color="success" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  staff.missingDays && staff.missingDays.length > 0
                                    ? staff.missingDays
                                        .map(d => new Date(d + 'T00:00:00').toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }))
                                        .join(', ')
                                    : 'No details'
                                }
                              >
                                <Chip
                                  label={`${staff.missingTotal} missing`}
                                  size="small"
                                  color={staff.missingTotal > 10 ? 'error' : 'warning'}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={staff.completionPercentage}
                                    color={staff.completionPercentage > 80 ? 'success' : 'warning'}
                                  />
                                </Box>
                                <Typography variant="caption" sx={{ minWidth: 40 }}>
                                  {staff.completionPercentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={`Email ${staff.name} about rota`}>
                                <IconButton size="small" color="primary" onClick={() => openRotaEmail(staff)}>
                                  <MailOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* SECTION 2: Leave Requests Summary - FILTERED BANK HOLIDAYS */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CalendarToday color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Leave Requests
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main' }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                      {data.leaveRequests.pendingCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                      Pending
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main' }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                      {data.leaveRequests.approvedFutureCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                      Approved (Future)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Coverage Warnings (Working Days Only)
              </Typography>

              {filteredCoverageWarnings.length === 0 ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  No coverage issues on working days
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="center"><strong>Coverage</strong></TableCell>
                        <TableCell align="right"><strong>Staff</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCoverageWarnings.map((warning, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(warning.date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={warning.coverageLevel}
                              size="small"
                              color={getCoverageChipColor(warning.coverageLevel)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {warning.workingCount}/{warning.totalStaff}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

 {/* SECTION 3: Balance Warnings - IMPROVED READABILITY */}
<Grid item xs={12}>
  <Card elevation={3}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Warning color="warning" />
        <Typography variant="h6" fontWeight="bold">
          Balance Warnings
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {/* High TIL (accurate data from staff balances) */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#ffebee', minHeight: 200, border: '2px solid #ef5350' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#c62828' }}>
              High TIL Balances
            </Typography>

            {staffBalances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {staffBalances
                  .map(p => {
                    const ent = p.leaveEntitlement || {};
                    const total = ent.timeInLieuHours || 0;
                    const used = ent.timeInLieuUsed || 0;
                    const balance = total - used;
                    return { ...p, balance };
                  })
                  .filter(p => p.balance > 0)
                  .sort((a, b) => b.balance - a.balance)
                  .map((p) => {
                    const til = p.balance;
                    const days = Math.floor(til / 12);
                    const hours = til % 12;
                    return (
                      <Box key={p._id} sx={{ bgcolor: 'white', p: 1, borderRadius: 1, border: '1px solid #ef5350' }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: '#1a1a1a' }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                          {til}h TIL ({days > 0 ? `${days}d` : ''} {hours > 0 ? `${hours}h` : ''})
                          {til > 16 && ' - Exceeds 16h guideline'}
                        </Typography>
                      </Box>
                    );
                  })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* High AL Remaining */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#fff3e0', minHeight: 200, border: '2px solid #ff9800' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#e65100' }}>
              High AL Remaining
            </Typography>
            {staffBalances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            ) : (() => {
              const now = new Date();
              const month = now.getMonth() + 1;
              const highAL = staffBalances
                .map(p => {
                  const total = p.leaveEntitlement?.annualLeaveCurrentYear || p.leaveEntitlement?.annualLeaveTotal || 0;
                  const used = p.leaveEntitlement?.annualLeaveUsed || 0;
                  const remaining = total - used;
                  const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
                  return { ...p, remaining, total, pct };
                })
                .filter(p => p.remaining > 0)
                .sort((a, b) => b.remaining - a.remaining);

              if (highAL.length === 0) {
                return <Typography variant="body2" color="text.secondary">All staff on track</Typography>;
              }

              return (
                <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {highAL.map(p => (
                    <Box key={p._id} sx={{ bgcolor: 'white', p: 1, borderRadius: 1, border: `1px solid ${p.pct > 60 && month >= 10 ? '#e65100' : '#ff9800'}` }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ color: '#1a1a1a' }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: p.pct > 60 && month >= 10 ? '#e65100' : '#666' }}>
                        {p.remaining}d left of {p.total}d ({p.pct}% unused)
                        {p.pct > 60 && month >= 10 ? ' — needs leave soon' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              );
            })()}
          </Paper>
        </Grid>

        {/* Expiring TIL */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#e3f2fd', minHeight: 200, border: '2px solid #2196f3' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0d47a1' }}>
              TIL Expiring Soon
            </Typography>
            {staffBalances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            ) : (() => {
              const now = new Date();
              const soon = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
              const expiring = staffBalances.flatMap(p =>
                (p.leaveEntitlement?.timeInLieuEntries || [])
                  .filter((e: any) => {
                    const exp = new Date(e.expiryDate);
                    return exp >= now && exp <= soon;
                  })
                  .map((e: any) => ({
                    name: p.name,
                    hours: e.hours,
                    expiryDate: new Date(e.expiryDate),
                    daysLeft: Math.ceil((new Date(e.expiryDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                  }))
              ).sort((a: any, b: any) => a.expiryDate - b.expiryDate);

              if (expiring.length === 0) {
                return <Typography variant="body2" color="text.secondary">No TIL expiring in next 30 days</Typography>;
              }

              return (
                <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {expiring.map((e: any, idx: number) => (
                    <Box key={idx} sx={{ bgcolor: 'white', p: 1, borderRadius: 1, border: `1px solid ${e.daysLeft <= 7 ? '#1565c0' : '#2196f3'}` }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ color: '#1a1a1a' }}>
                        {e.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: e.daysLeft <= 7 ? '#1565c0' : '#666' }}>
                        {e.hours}h expires {e.expiryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ({e.daysLeft}d)
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
</Grid>

        {/* SECTION 4: Performance Coverage - ACTIONABLE WITH LINKS */}
        <Grid item xs={12} lg={8}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Event color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Upcoming Performances (Next 30 Days)
                </Typography>
              </Stack>

              {data.performances.missingStaff.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {data.performances.missingStaff.length} performance(s) have no staff assigned!
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Venue</strong></TableCell>
                      <TableCell><strong>Band/Artist</strong></TableCell>
                      <TableCell align="center"><strong>Staff</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.performances.upcoming.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No upcoming performances
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.performances.upcoming.map((perf) => {
                        const isHoliday = !isWorkingDay(perf.date);
                        const holidayName = getHolidayName(perf.date);
                        const isExpanded = expandedPerformance === perf.performanceId;
                        
                        return (
                          <React.Fragment key={perf.performanceId}>
                            <TableRow 
                              sx={{ 
                                bgcolor: perf.staffCount === 0 ? '#ffebee' : 'inherit',
                                '& .MuiTypography-root': {
                                  color: perf.staffCount === 0 ? '#111' : 'inherit',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(perf.date)}
                                </Typography>
                                {isHoliday && holidayName && (
                                  <Typography variant="caption" color="secondary.main">
                                    {holidayName}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {perf.venue}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {perf.bandName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                  <Chip 
                                    label={perf.staffCount}
                                    size="medium"
                                    sx={{
                                      fontSize: '1.1rem',
                                      fontWeight: 'bold',
                                      minWidth: 50,
                                      bgcolor: perf.staffCount === 0 ? '#ef5350' : 'default',
                                      color: perf.staffCount === 0 ? 'white' : 'inherit'
                                    }}
                                  />
                                  {perf.staffCount > 0 && (
                                    <Button
                                      size="small"
                                      onClick={() => setExpandedPerformance(isExpanded ? null : perf.performanceId)}
                                    >
                                      {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </Button>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                <Stack spacing={0.5}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    fullWidth
                                    endIcon={<OpenInNew />}
                                    onClick={() => navigate(`/admin/performances/${perf.performanceId}`)}
                                  >
                                    Event
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    endIcon={<Schedule />}
                                    onClick={() => navigate(`/admin/rota`)}
                                  >
                                    Rota
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expandable Staff Details Row */}
                            {isExpanded && perf.staff && perf.staff.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ bgcolor: '#2d3748', py: 2 }}>
                                  <Box sx={{ pl: 4 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'white' }}>
                                      Assigned Staff:
                                    </Typography>
                                    <Stack spacing={1}>
                                      {perf.staff.map((staffMember) => (
                                        <Box 
                                          key={staffMember.staffId}
                                          sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 1,
                                            bgcolor: '#1a202c',
                                            borderRadius: 1,
                                            border: '1px solid #4a5568'
                                          }}
                                        >
                                          <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 150, color: 'white' }}>
                                            {staffMember.name}
                                          </Typography>
                                          <Chip 
                                            label={staffMember.role || 'Role pending'} 
                                            size="small" 
                                            color={staffMember.role ? 'primary' : 'default'}
                                            variant="outlined"
                                            sx={{ 
                                              color: 'white',
                                              borderColor: staffMember.role ? '#4299e1' : '#718096'
                                            }}
                                          />
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* SECTION 5: Artist Database Stats */}
        <Grid item xs={12} lg={4}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <MusicNote color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Artist Database
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main' }}>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                    {data.artistStats.totalArtists}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    Total Artists
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main' }}>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                    {data.artistStats.recentlyAdded}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    Added Last 7 Days
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main' }}>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                    {data.artistStats.withUpcomingPerformances}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    With Upcoming Shows
                  </Typography>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer with last updated */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date(data.generatedAt).toLocaleString('en-GB')}
        </Typography>
      </Box>

      {/* Rota Reminder Email Dialog */}
      <Dialog open={rotaEmailDialog.open} onClose={() => setRotaEmailDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Email {rotaEmailDialog.staffName} — Rota Reminder</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Subject"
            value={rotaEmailDialog.subject}
            onChange={e => setRotaEmailDialog(d => ({ ...d, subject: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Message"
            value={rotaEmailDialog.message}
            onChange={e => setRotaEmailDialog(d => ({ ...d, message: e.target.value }))}
            fullWidth
            multiline
            rows={8}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRotaEmailDialog(d => ({ ...d, open: false }))} disabled={rotaEmailSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendRotaEmail}
            disabled={rotaEmailSending || !rotaEmailDialog.subject.trim() || !rotaEmailDialog.message.trim()}
          >
            {rotaEmailSending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OverviewTab;